/**
 * PSI staff's favorite reports, the same in every connection (hooks/sharedPrefs.ts,
 * SHARED_ONLY_KEYS.REPORT_FAVORITES).
 *
 * Everyone else's favorites live per connection in tblPM_User_ReportFavorite, shared with the
 * desktop and keyed by its reports-tree node (reportFavoriteKeys.ts). Across connections neither
 * that key nor tblRS_Report.PKey_RS_Report is something to rely on, and tblRS_Report.ProgramName
 * is no help (it reads "Dietech" or is empty on every row). So the shared list is keyed by the
 * website's own card slug ("census", "tray-tickets"): the card identity the catalog resolves from
 * each connection's report row (useReportCatalog, findReportConfig). A favorite shows in a
 * connection only when that connection's catalog has the card, so a report the connection lacks,
 * hides or does not allow simply does not appear there.
 */

export interface SharedReportFavorite {
  /** The catalog card's slug. */
  slug: string;
  /** The card's name when it was starred (for reading the stored list; the card shows its own). */
  name: string;
  /** Star order: lower first. */
  priority: number;
}

/** The stored list, or undefined when there is none (or it is not a list). Bad entries are dropped. */
export function parseSharedFavorites(raw: unknown): SharedReportFavorite[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const seen = new Set<string>();
  const out: SharedReportFavorite[] = [];
  raw.forEach((entry, i) => {
    if (!entry || typeof entry !== 'object') return;
    const e = entry as Partial<SharedReportFavorite>;
    if (typeof e.slug !== 'string' || !e.slug || seen.has(e.slug)) return;
    seen.add(e.slug);
    out.push({
      slug: e.slug,
      name: typeof e.name === 'string' ? e.name : '',
      priority: typeof e.priority === 'number' && Number.isFinite(e.priority) ? e.priority : i,
    });
  });
  return out;
}

/** The slugs of the cards this connection has that are favorites. */
export function sharedFavoriteSlugs(
  favorites: readonly SharedReportFavorite[],
  rows: ReadonlyArray<{ slug: string }>,
): Set<string> {
  const wanted = new Set(favorites.map((f) => f.slug));
  return new Set(rows.filter((r) => wanted.has(r.slug)).map((r) => r.slug));
}

/** Stars or unstars a card. `added` says which. */
export function toggleSharedFavorite(
  favorites: readonly SharedReportFavorite[],
  card: { slug: string; name: string },
): { next: SharedReportFavorite[]; added: boolean } {
  if (favorites.some((f) => f.slug === card.slug)) {
    return { next: favorites.filter((f) => f.slug !== card.slug), added: false };
  }
  const priority = favorites.reduce((max, f) => Math.max(max, f.priority), -1) + 1;
  return { next: [...favorites, { slug: card.slug, name: card.name, priority }], added: true };
}

/** First use: the current connection's favorites become the shared list. */
export function seedSharedFavorites(cards: ReadonlyArray<{ slug: string; name: string }>): SharedReportFavorite[] {
  return cards.map((c, i) => ({ slug: c.slug, name: c.name, priority: i }));
}
